import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { formatMoney, toISODate } from "../utils/driverDeliveries";

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    first_name?: string;
    last_name?: string;
    rol?: string;
  };
  error?: string;
};

type LegacyDriverRow = {
  fecha_colecta?: string;
  estado?: string;
  precio_chofer?: string | number;
  porcentaje_chofer?: string | number;
  [key: string]: unknown;
};

type LegacyDriverResponse = {
  ok?: boolean;
  result?: LegacyDriverRow[];
  error?: string;
};

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const cardsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "20px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "18px",
};

const featureCardStyle: React.CSSProperties = {
  ...cardStyle,
  minHeight: "154px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const featureTopStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
};

const featureIconStyle: React.CSSProperties = {
  width: "38px",
  height: "38px",
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  fontSize: "18px",
  flexShrink: 0,
};

const featureTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const featureTextStyle: React.CSSProperties = {
  margin: "10px 0 18px 0",
  fontSize: "13px",
  lineHeight: 1.45,
  color: "var(--text-secondary)",
};

const featureValueStyle: React.CSSProperties = {
  marginTop: "14px",
  fontSize: "26px",
  fontWeight: 900,
  color: "var(--text-primary)",
  letterSpacing: "-0.04em",
};

const buttonStyle: React.CSSProperties = {
  minHeight: "38px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--primary)",
  color: "white",
  padding: "0 14px",
  fontWeight: 800,
  cursor: "pointer",
};

const ghostButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
};

const chartCardStyle: React.CSSProperties = {
  ...cardStyle,
  padding: "18px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "17px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "13px",
  lineHeight: 1.5,
};

const chartGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: "12px",
  alignItems: "end",
  marginTop: "18px",
  minHeight: "220px",
};

const barWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px",
  height: "220px",
};

const barStyle = (height: number): React.CSSProperties => ({
  width: "100%",
  maxWidth: "46px",
  minHeight: height > 0 ? "14px" : "6px",
  height: `${height}%`,
  borderRadius: "14px 14px 8px 8px",
  background:
    height > 0
      ? "linear-gradient(180deg, var(--primary) 0%, var(--primary-soft) 100%)"
      : "var(--surface-alt)",
  border: "1px solid var(--border)",
});

const barValueStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const barLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  fontWeight: 700,
};

const emptyStateStyle: React.CSSProperties = {
  padding: "32px 20px",
  borderRadius: "18px",
  border: "1px dashed var(--border)",
  textAlign: "center",
  color: "var(--text-secondary)",
  marginTop: "18px",
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeLegacyName = (value: string): string => {
  return value.trim().replace(/\s+/g, " ");
};

const buildFullName = (
  firstName?: string,
  lastName?: string,
  username?: string | null
): string => {
  const fullName = [firstName || "", lastName || ""]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" ");

  return fullName || username || "Driver";
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;

  const raw = value.trim();
  if (!raw) return 0;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let normalized = raw;

  if (hasComma && hasDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = raw.replace(",", ".");
  } else if (hasDot) {
    normalized = /^\d+\.\d+$/.test(raw) ? raw : raw.replace(/\./g, "");
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCommissionPercentage = (value: unknown): number => {
  const numeric = toNumber(value);

  if (!numeric) return 0;
  if (numeric > 0 && numeric <= 1) return numeric;
  if (numeric > 1) return numeric / 100;

  return 0;
};

const getNetAmount = (row: LegacyDriverRow): number => {
  const bruto = toNumber(row.precio_chofer);
  const commission = normalizeCommissionPercentage(row.porcentaje_chofer);
  return bruto - bruto * commission;
};

const isDelivered = (estado?: string): boolean => {
  return safeString(estado).toLowerCase().includes("entreg");
};

const HomeDriver = () => {
  const { token, userId, username, firstName, lastName } = useAuth();

  const [driverFullName, setDriverFullName] = useState<string>(
    buildFullName(firstName || "", lastName || "", username)
  );
  const [legacyDriverName, setLegacyDriverName] = useState<string>("");
  const [rows, setRows] = useState<LegacyDriverRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const fetchDriverHome = async () => {
    if (!token || !userId || !username) {
      setError("No se pudo identificar el driver autenticado.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const userResponse = await authFetch(`/v2/users/${userId}`, { token });
      const userPayload = (await userResponse
        .json()
        .catch(() => ({}))) as CurrentUserResponse;

      const resolvedFullName =
        userResponse.ok && userPayload?.user
          ? buildFullName(
              userPayload.user.first_name || "",
              userPayload.user.last_name || "",
              userPayload.user.username || username
            )
          : buildFullName(firstName || "", lastName || "", username);

      const resolvedLegacyName = normalizeLegacyName(resolvedFullName);

      console.log("Legacy driver lookup:", {
        authContext: {
          userId,
          username,
          firstName,
          lastName,
        },
        userEndpointOk: userResponse.ok,
        userEndpointUser: userPayload?.user,
        resolvedFullName,
        resolvedLegacyName,
      });

      setDriverFullName(resolvedFullName);
      setLegacyDriverName(resolvedLegacyName);

      const response = await authFetch("/v1/data/driver/me", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: resolvedLegacyName,
        }),
      });

      const payload = (await response
        .json()
        .catch(() => ({}))) as LegacyDriverResponse;

      if (!response.ok || payload?.ok === false) {
        if (response.status === 401) {
          throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
        }

        if (response.status === 403) {
          throw new Error(
            "Tu usuario no tiene permiso para ver el panel de driver."
          );
        }

        throw new Error(payload?.error || "No se pudieron cargar los envíos.");
      }

      setRows(Array.isArray(payload.result) ? payload.result : []);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Error cargando panel driver.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, username, firstName, lastName]);

  const visibleRows = useMemo(() => rows, [rows]);

  const summary = useMemo(() => {
    const total = visibleRows.length;
    const delivered = visibleRows.filter((row) => isDelivered(row.estado)).length;
    const pending = Math.max(total - delivered, 0);
    const net = visibleRows.reduce((acc, row) => acc + getNetAmount(row), 0);

    return {
      total,
      delivered,
      pending,
      net,
    };
  }, [visibleRows]);

  const chartData = useMemo(() => {
    const grouped = new Map<string, number>();

    visibleRows.forEach((row) => {
      const iso = toISODate(safeString(row.fecha_colecta));
      if (!iso) return;
      grouped.set(iso, (grouped.get(iso) || 0) + 1);
    });

    return Array.from(grouped.entries())
      .map(([iso, count]) => ({
        iso,
        label: iso.split("-").reverse().join("/"),
        count,
      }))
      .sort((a, b) => a.iso.localeCompare(b.iso));
  }, [visibleRows]);

  const maxCount = Math.max(...chartData.map((item) => item.count), 1);

  const handleDownloadWeeklyDetail = async () => {
    if (!token || !legacyDriverName) return;

    try {
      setDownloading(true);

      const response = await authFetch("/v1/data/driver/print", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: legacyDriverName,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
        }

        if (response.status === 403) {
          throw new Error(
            "Tu usuario no tiene permiso para descargar el detalle semanal. Backend debe permitir DRIVER en /v1/data/driver/print."
          );
        }

        const text = await response.text();
        throw new Error(text || "No se pudo descargar el detalle semanal.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = legacyDriverName.replace(/\s+/g, "_").toLowerCase();

      link.href = objectUrl;
      link.download = `detalle_semanal_${safeName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
      alert(
        err instanceof Error
          ? err.message
          : "No se pudo descargar el detalle semanal."
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <section style={cardsGridStyle}>
        <div style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Facturación</h2>
              <div style={featureIconStyle}>◷</div>
            </div>

            <div style={featureValueStyle}>$ {formatMoney(summary.net)}</div>
          </div>

          <button
            style={buttonStyle}
            onClick={handleDownloadWeeklyDetail}
            disabled={downloading || loading || !legacyDriverName}
          >
            {downloading ? "Descargando..." : "Descargar detalle"}
          </button>
        </div>

        <div style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Reclamos</h2>
              <div style={featureIconStyle}>!</div>
            </div>

            <div style={featureValueStyle}>Próximamente</div>
            <p style={featureTextStyle}>
              Informar reclamos, envíos faltantes u observaciones operativas.
            </p>
          </div>

          <button style={ghostButtonStyle} disabled>
            Informar reclamo
          </button>
        </div>

        <div style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Turbo</h2>
              <div style={featureIconStyle}>🚀</div>
            </div>

            <div style={featureValueStyle}>Próximamente</div>
            <p style={featureTextStyle}>
              Postularse como chofer para envíos turbo.
            </p>
          </div>

          <button style={ghostButtonStyle} disabled>
            Postularme
          </button>
        </div>
      </section>

      <section style={chartCardStyle}>
        <h2 style={sectionTitleStyle}>Envíos cargados en sistema</h2>
        <p style={sectionTextStyle}>
          Distribución de los envíos asociados a tu usuario legacy.
        </p>

        {loading ? (
          <div style={emptyStateStyle}>Cargando información...</div>
        ) : error ? (
          <div style={emptyStateStyle}>{error}</div>
        ) : visibleRows.length === 0 ? (
          <div style={emptyStateStyle}>
            No hay envíos cargados para el chofer legacy:{" "}
            <strong>{legacyDriverName || driverFullName}</strong>.
          </div>
        ) : (
          <div style={chartGridStyle}>
            {chartData.map((item) => {
              const height = Math.max(
                (item.count / maxCount) * 100,
                item.count > 0 ? 10 : 4
              );

              return (
                <div key={item.iso} style={barWrapStyle}>
                  <div style={barValueStyle}>{item.count}</div>
                  <div style={barStyle(height)} />
                  <div style={barLabelStyle}>{item.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default HomeDriver;